// Tag a teammate in a message box: type @ and pick from the list (arrows, Enter or Tab, Esc). The box shows
// "@Sam Martin"; `tagged(text)` turns it into the stored tag before sending (@upm/domain linkMentions), and
// MessageBody (feedback/Formatting.tsx) shows tags highlighted.
import { useId, useState, type KeyboardEvent, type RefObject } from 'react';
import { linkMentions } from '@upm/domain';
import { Avatar, cx, initialsOf } from '../ui';

export type Teammate = { id: string; name: string };

/** "@sa" or "@sam ma" right before the caret, at the start or after a space or bracket. */
const TYPING = /(^|[\s(])@([^\s@]{0,30}(?: [^\s@]{0,30})?)$/;

function matches(team: readonly Teammate[], q: string) {
  const query = q.toLowerCase();
  return team.filter((p) => {
    const name = p.name.toLowerCase();
    return name.startsWith(query) || name.split(/\s+/).some((w) => w.startsWith(query));
  }).slice(0, 6);
}

/**
 * The @ picker for a textarea. Spread `inputProps` on the textarea (after its own value), call `onKeyDown` first in
 * its key handler (true = handled), and put `menu` right after it inside a `relative` box.
 */
export function useMentionPicker(box: RefObject<HTMLTextAreaElement | null>, setText: (text: string) => void, team: readonly Teammate[]) {
  const [typing, setTyping] = useState<{ start: number; q: string } | null>(null);
  const [active, setActive] = useState(0);
  const id = useId();
  const options = typing ? matches(team, typing.q) : [];
  const open = options.length > 0;

  const sync = () => {
    const el = box.current;
    if (!el || el.selectionStart !== el.selectionEnd) { setTyping(null); return; }
    const m = el.value.slice(0, el.selectionStart).match(TYPING);
    if (!m) { setTyping(null); return; }
    setTyping((cur) => {
      const next = { start: el.selectionStart - m[2]!.length - 1, q: m[2]! };
      if (cur?.q !== next.q) setActive(0);
      return next;
    });
  };

  const pick = (p: Teammate) => {
    const el = box.current;
    if (!el || !typing) return;
    const insert = `@${p.name} `;
    const value = el.value.slice(0, typing.start) + insert + el.value.slice(el.selectionStart);
    const caret = typing.start + insert.length;
    setText(value);
    setTyping(null);
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(caret, caret); });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): boolean => {
    if (!open) return false;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (a + (e.key === 'ArrowDown' ? 1 : options.length - 1)) % options.length);
      return true;
    }
    if ((e.key === 'Enter' && !e.metaKey && !e.ctrlKey && !e.shiftKey) || e.key === 'Tab') {
      e.preventDefault();
      pick(options[Math.min(active, options.length - 1)]!);
      return true;
    }
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setTyping(null); return true; }
    return false;
  };

  // The box stays a textbox (screen readers keep its editing behaviour); the list is announced through these.
  const inputProps = {
    'aria-autocomplete': 'list' as const,
    'aria-controls': open ? `${id}-list` : undefined,
    'aria-activedescendant': open ? `${id}-${Math.min(active, options.length - 1)}` : undefined,
    onSelect: sync,
    onBlur: () => setTyping(null),
  };

  const menu = open ? (
    <ul id={`${id}-list`} role="listbox" aria-label="Teammates"
      className="absolute left-2 top-full z-30 m-0 mt-1 w-64 list-none rounded-[10px] border border-line bg-white p-1 shadow-lg">
      {options.map((p, i) => (
        <li key={p.id} id={`${id}-${i}`} role="option" aria-selected={i === active}
          onMouseDown={(e) => { e.preventDefault(); pick(p); }} onMouseEnter={() => setActive(i)}
          className={cx('flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm', i === active && 'bg-brand-soft text-brand')}>
          <Avatar initials={initialsOf(p.name)} size={22} tone="soft" />{p.name}
        </li>
      ))}
    </ul>
  ) : null;

  /** The text to send: every "@Full Name" of a teammate becomes a tag. */
  const tagged = (text: string) => linkMentions(text, team);

  return { inputProps, onKeyDown, menu, tagged, sync };
}
